import React, { useEffect, useState } from 'react';
import { InboxOutlined } from '@ant-design/icons';
import { Modal, Form, Input, message, Upload } from 'antd';
import axiosInstance from '../../utils/axios';

const { Dragger } = Upload;

const PartnerModal = ({ open, setOpen, partners, setPartner, selectedId }) => {
  const [form] = Form.useForm();
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  useEffect(() => {
    if (open) {
      if (selectedId !== null) {
        const item = partners.find(p => p.id === selectedId);
        setSelectedItem(item);
        if (item) {
          form.setFieldsValue({ name: item.name });
          setFile({ uid: '-1', name: 'logo', status: 'done', url: item.logo });
        }
      } else {
        setSelectedItem(null);
        form.resetFields();
        setFile(null);
      }
    }
  }, [open, selectedId, partners]);

  const handleCancel = () => {
    setOpen(false);
    form.resetFields();
    setFile(null);
    setSelectedItem(null);
  };

  const handleFinish = async (values) => {
    setLoading(true);
    const formData = new FormData();
    formData.append("name", values.name);

    if (file && file.originFileObj) {
      formData.append("logo", file.originFileObj);
    }

    try {
      let response;
      if (selectedItem) {
        response = await axiosInstance.patch(`core/partner/${selectedItem.id}/`, formData);
        setPartner(prev => prev.map(p => (p.id === selectedItem.id ? response.data : p)));
        message.success("Partner updated successfully!");
      } else {
        if (!file || !file.originFileObj) {
          message.error("Please upload a logo");
          setLoading(false);
          return;
        }
        response = await axiosInstance.post(`core/partner/`, formData);
        setPartner(prev => [...prev, response.data]);
        message.success("Partner added successfully!");
      }

      form.resetFields();
      setFile(null);
      setOpen(false);
    } catch (error) {
      message.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const MAX_FILE_SIZE_MB = 1;

  const uploadProps = {
    name: 'logo',
    multiple: false,
    beforeUpload: (file) => {
      const isSupportedImage =
        ['image/jpeg', 'image/png', 'image/webp', 'image/avif'].includes(file.type);

      if (!isSupportedImage) {
        message.error('Only JPG, PNG, WEBP, or AVIF images are allowed!');
        return Upload.LIST_IGNORE;
      }

      const isLt1MB = file.size / 1024 / 1024 < MAX_FILE_SIZE_MB;
      if (!isLt1MB) {
        message.error('Image must be smaller than 1MB!');
        return Upload.LIST_IGNORE;
      }

      setFile({ ...file, originFileObj: file });
      return false;
    },
    onRemove: () => {
      setFile(null);
    },
    fileList: file ? [file] : [],
  };

  return (
    <Modal
      title={selectedItem ? "Edit Partner" : "Add new partner"}
      open={open}
      onCancel={handleCancel}
      destroyOnClose
      okButtonProps={{
        autoFocus: true,
        htmlType: 'submit',
        loading: loading,
        disabled: loading,
      }}
      onOk={() => form.submit()}
    >
      <Form
        layout="vertical"
        form={form}
        name="partner_form"
        onFinish={handleFinish}
      >
        <Form.Item
          label="Name"
          name="name"
          rules={[{ required: true, message: 'Please enter a name' }]}
        >
          <Input placeholder="Enter Partner name" />
        </Form.Item>

        <Form.Item
          label="Upload Logo"
          required
        >
          <Dragger {...uploadProps}>
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">Click or drag image to upload</p>
            <p className="ant-upload-hint">Only JPG, PNG, WEBP, AVIF — Max 1MB</p>
          </Dragger>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default PartnerModal;
